<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Advancing Bills</h1>
        <button @click="printAdvancingBills" class="print-btn">
          <i class="pi pi-print"></i> Print
        </button>
      </div>

      <DataTable 
        :value="advancingBills" 
        :paginator="true" 
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        :loading="loading"
        stripedRows
        showGridlines
        responsiveLayout="scroll"
        :globalFilterFields="['title', 'short_title', 'nos', 'authors']"
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <h3>Advancing Bills ({{ advancingBills.length }})</h3>
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search bills..." />
            </IconField>
          </div>
        </template>
      
        <Column field="title" header="Bill Title" sortable :showFilterMatchModes="false">
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search title" />
          </template>
        </Column>
        
        <Column field="short_title" header="Short Title" sortable :showFilterMatchModes="false">
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search short title" />
          </template>
        </Column>
        
        <Column field="nos" header="Committee Report No" sortable :showFilterMatchModes="false">
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search report no" />
          </template>
        </Column>
        
        <Column field="authors" header="Authors" sortable :showFilterMatchModes="false">
          <template #body="{ data }">
            {{ formatAuthors(data.authors) }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search authors" />
          </template>
        </Column>
        
        <Column header="Source/Link">
          <template #body="{ data }">
            <a v-if="data.source_link" :href="getFullUrl(data.source_link)" target="_blank" class="link-button">
              <i class="pi pi-external-link"></i>
              View Source
            </a>
            <span v-else>N/A</span>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import api from '../axios.js'

const advancingBills = ref([])
const loading = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  title: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  short_title: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  nos: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  authors: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const loadAdvancingBills = async () => {
  try {
    loading.value = true
    const response = await api.get('/advancing-bills')
    advancingBills.value = response.data
  } catch (error) {
    console.error('Error loading advancing bills:', error)
    advancingBills.value = []
  } finally {
    loading.value = false
  }
}

const getFullUrl = (url) => {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `https://${url}`
}

const formatAuthors = (authors) => {
  if (!authors) return 'N/A'
  try {
    const parsed = JSON.parse(authors)
    return Array.isArray(parsed) ? parsed.join(', ') : authors
  } catch {
    return authors
  }
}

const printAdvancingBills = async () => {
  try {
    const response = await api.get('/advancing-bills/print', { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (error) {
    console.error('Error printing advancing bills:', error)
  }
}

onMounted(() => {
  loadAdvancingBills()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}


.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #059669;
}

.page-header h1 {
  color: #059669;
  font-size: 2rem;
  font-weight: 700;
}

.print-btn {
  background: #059669;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
}

.print-btn:hover {
  background: #047857;
  transform: translateY(-1px);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #059669;
  margin: 0;
}
.link-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #059669;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.link-button:hover {
  background: #047857;
  text-decoration: none;
  color: white;
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }
}
</style>